/**
 * AML/PEP Screening API Service
 * Handles all communication with AML backend endpoints
 */

import { authFetch, getLocalApiUrl } from '../config/api'

class AMLService {
  /**
   * Search persons by name
   * @param {string} name - Name to search for
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results
   */
  async searchPersons(name, options = {}) {
    try {
      const params = new URLSearchParams({
        name,
        limit: options.limit || 50,
        confidenceThreshold: options.confidenceThreshold || 60,
        includeInactive: options.includeInactive || false,
        ...options
      })

      const response = await authFetch(getLocalApiUrl(`/api/aml/search/persons?${params}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error searching persons:', error)
      throw new Error(`Failed to search persons: ${error.message}`)
    }
  }

  /**
   * Advanced person search (name + optional filters like country, DOB, ID)
   * @param {string} name
   * @param {Object} options
   */
  async searchPersonsAdvanced(name, options = {}) {
    try {
      const params = new URLSearchParams({
        name,
        limit: options.limit || 50,
        includeInactive: options.includeInactive || true,
        country: options.country || '',
        dateOfBirth: options.dateOfBirth || '',
        idNumber: options.idNumber || '',
        idType: options.idType || ''
      })
      if (options.nameMatch === 'exact') params.set('nameMatch', 'exact')

      const response = await authFetch(getLocalApiUrl(`/api/aml/search/persons/advanced?${params}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error in advanced person search:', error)
      throw new Error(`Failed to search persons (advanced): ${error.message}`)
    }
  }

  /**
   * Search entities by name
   * @param {string} name - Name to search for
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results
   */
  async searchEntities(name, options = {}) {
    try {
      const params = new URLSearchParams({
        name,
        limit: options.limit || 50,
        confidenceThreshold: options.confidenceThreshold || 60,
        includeInactive: options.includeInactive || false,
        ...options
      })

      const response = await authFetch(getLocalApiUrl(`/api/aml/search/entities?${params}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error searching entities:', error)
      throw new Error(`Failed to search entities: ${error.message}`)
    }
  }

  /**
   * Search both persons and entities
   * @param {string} name - Name to search for
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Combined search results
   */
  async searchAll(name, options = {}) {
    try {
      const params = new URLSearchParams({
        name,
        limit: String(options.limit || 50),
        confidenceThreshold: String(options.confidenceThreshold ?? 60),
        includeInactive: String(options.includeInactive ?? true)
      })
      if (options.country) params.set('country', options.country)
      if (options.dateOfBirth) params.set('dateOfBirth', options.dateOfBirth)
      if (options.idNumber) params.set('idNumber', options.idNumber)
      if (options.idType) params.set('idType', options.idType)
      if (options.nameMatch === 'exact') params.set('nameMatch', 'exact')

      const response = await authFetch(getLocalApiUrl(`/api/aml/search/all?${params}`), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}))
        throw new Error(errBody?.error || `HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error in combined search:', error)
      throw new Error(`Failed to search all: ${error.message}`)
    }
  }

  /**
   * Screen a patron against AML databases
   * @param {string} patronName - Patron name to screen
   * @param {Object} patronData - Additional patron data
   * @returns {Promise<Object>} Screening results
   */
  async screenPatron(patronName, patronData = {}) {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/aml/screen-patron`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: patronName,
          patronData,
          timestamp: new Date().toISOString()
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error screening patron:', error)
      throw new Error(`Failed to screen patron: ${error.message}`)
    }
  }

  /**
   * Batch screen multiple patrons
   * @param {Array} patrons - Array of patron objects to screen
   * @returns {Promise<Object>} Batch screening results
   */
  async batchScreenPatrons(patrons) {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/aml/batch-screen`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patrons,
          timestamp: new Date().toISOString()
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error in batch screening:', error)
      throw new Error(`Failed to batch screen patrons: ${error.message}`)
    }
  }

  /**
   * Get person details by ID
   * @param {string} personId - Person ID
   * @returns {Promise<Object>} Person details
   */
  async getPersonDetails(personId) {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/aml/person/${personId}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error getting person details:', error)
      throw new Error(`Failed to get person details: ${error.message}`)
    }
  }

  /**
   * Get entity details by ID
   * @param {string} entityId - Entity ID
   * @returns {Promise<Object>} Entity details
   */
  async getEntityDetails(entityId) {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/aml/entity/${entityId}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error getting entity details:', error)
      throw new Error(`Failed to get entity details: ${error.message}`)
    }
  }

  /**
   * Get patron screening history
   * @param {string} patronId - Patron ID
   * @returns {Promise<Object>} Screening history
   */
  async getPatronHistory(patronId) {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/aml/patron/${patronId}/history`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error getting patron history:', error)
      throw new Error(`Failed to get patron history: ${error.message}`)
    }
  }

  /**
   * Update match status (approve/reject/investigate)
   * @param {string} matchId - Match ID
   * @param {string} status - New status
   * @param {string} notes - Optional notes
   * @returns {Promise<Object>} Update result
   */
  async updateMatchStatus(matchId, status, notes = '') {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/aml/match/${matchId}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status,
          notes,
          timestamp: new Date().toISOString()
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error updating match status:', error)
      throw new Error(`Failed to update match status: ${error.message}`)
    }
  }

  /**
   * Get AML dashboard statistics
   * @returns {Promise<Object>} Dashboard stats
   */
  async getDashboardStats() {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/aml/dashboard`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error getting dashboard stats:', error)
      throw new Error(`Failed to get dashboard stats: ${error.message}`)
    }
  }

  /**
   * Get system statistics
   * @returns {Promise<Object>} System stats
   */
  async getSystemStats() {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/aml/stats`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error getting system stats:', error)
      throw new Error(`Failed to get system stats: ${error.message}`)
    }
  }

  /**
   * Export search results
   * @param {Object} searchResults - Results to export
   * @param {string} format - Export format (csv, json, pdf)
   * @returns {Promise<Blob>} Export file
   */
  async exportResults(searchResults, format = 'csv') {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/aml/export`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          results: searchResults,
          format,
          timestamp: new Date().toISOString()
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.blob()
    } catch (error) {
      console.error('Error exporting results:', error)
      throw new Error(`Failed to export results: ${error.message}`)
    }
  }

  /**
   * Health check for AML service
   * @returns {Promise<Object>} Health status
   */
  async healthCheck() {
    try {
      const response = await authFetch(getLocalApiUrl('/health'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error checking health:', error)
      throw new Error(`Failed to check health: ${error.message}`)
    }
  }

  /**
   * Get database status
   * @returns {Promise<Object>} Database status
   */
  async getDatabaseStatus() {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/database/status`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error getting database status:', error)
      throw new Error(`Failed to get database status: ${error.message}`)
    }
  }
}

// Export singleton instance
const amlService = new AMLService()
export default amlService

// Export class for testing
export { AMLService }